import { useEffect } from 'react';
import { createStore } from 'zustand';
import { StoreApi } from 'zustand';
import { StateCreator } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { useSearchParams } from '@spazious/dollhouse-router';
import { useStore } from '@spazious/dollhouse-store';
import { ModeTypeEnum } from '@spazious/dollhouse-types';

export interface StateParams {
  spaceId: string | null;
  setupGraphId: string | null;
  mode: ModeTypeEnum;
  hotelId?: string | null;
  /**
   * When true, the editor should hide the save button and other UI elements.
   */
  fullIntegrationMode?: boolean;
}

export type RootStateParams = StateParams & {
  set: StoreApi<StateParams>['setState'];
  get: StoreApi<StateParams>['setState'];
};

export const sliceParams: StateCreator<StateParams> = () => ({
  // Primary state
  spaceId: new URLSearchParams(window.location.search).get('space') ?? null, // Exposed in search state
  setupGraphId: new URLSearchParams(window.location.search).get('graph') ?? null, // Exposed in search state
  hotelId: new URLSearchParams(window.location.search).get('hotel') ?? null,
  fullIntegrationMode:
    new URLSearchParams(window.location.search).get('fullIntegrationMode') === 'true',
  // Crucial but not desired state
  mode: ModeTypeEnum.ReadOnly,
  // Temporary state
  spaceUUID: null,
});

export const paramsStoreInitializer = (
  set: RootStateParams['set'],
  get: () => RootStateParams,
  store: StoreApi<RootStateParams>
) => {
  return {
    set,
    get,
    ...sliceParams(set, get, store),
  };
};

export const paramsStore = createStore<RootStateParams>(
  import.meta.env.DEV
    ? (devtools<RootStateParams>(paramsStoreInitializer, {
        // TOO change Spazious prefix by SPZ in all occurrences
        name: 'Spazious Dollhouse Params',
      }) as any)
    : paramsStoreInitializer
);

export interface ParamsHook {
  (): RootStateParams;
  <U>(selector: (state: RootStateParams) => U, equalityFn?: () => any): U;
}

export interface ParamsHook {
  (): RootStateParams;
  <U>(selector: (state: RootStateParams) => U, equalityFn?: () => any): U;
}

export const useParams: ParamsHook = <U>(selector?: any, equalityFn?: any): U => {
  const setPlannerState = useStore(({ set }) => set);
  const [searchParams, setSearchParams] = useSearchParams({
    space: '',
    graph: '',
  });

  // Set initial state from search state
  useEffect(() => {
    const graph = searchParams.get('graph');
    if (graph) {
      paramsStore.setState({ setupGraphId: searchParams.get('graph') });
    }
    const space = searchParams.get('space');
    if (space) {
      paramsStore.setState({ spaceId: searchParams.get('space') });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return paramsStore.subscribe(() => {
      const { spaceId, setupGraphId } = paramsStore.getState();
      // Update search state when state changes
      setSearchParams(
        () =>
          Object.entries({
            space: !setupGraphId ? spaceId ?? '' : '',
            graph: setupGraphId ?? '',
          }).filter(([, value]) => !!value),
        {
          replace: true,
        }
      );
    });
  }, [setPlannerState, setSearchParams]);

  return useStoreWithEqualityFn(paramsStore, selector, equalityFn);
};
